import { colors } from 'fitify-ui'
import { TextStyles } from 'fitify-ui-landing/src/components/Text'
import {
  biggerPhone,
  phone,
  tabletPortrait,
} from 'fitify-ui-landing/src/theme/breakpoints'
import styled, { css, keyframes } from 'styled-components'

interface StyledNewsletterProps {
  $isSuccess?: boolean
  $hasError?: boolean
}

const spinner = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const StyledNewsletter = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 48px;
  border: 1px solid ${colors.Gray200};
  border-radius: 24px;
  background: ${colors.White};
  gap: 24px;

  ${biggerPhone(css`
    flex-direction: column;
    padding: 24px;
    border-radius: 16px;
  `)};
`

export const StyledNewsletterCopy = styled.div`
  width: 100%;
  max-width: 432px;
  ${TextStyles} {
    b {
      color: ${colors.Black};
    }
  }
`

export const StyledNewsletterForm = styled.form<StyledNewsletterProps>`
  position: relative;
  display: flex;
  width: 100%;
  min-width: 440px;
  align-items: center;
  justify-content: flex-start;
  border-radius: 100px;
  background-color: ${(props) =>
    props.$hasError ? colors.Red50 : colors.Gray200};
  text-align: center;
  transition: background-color 0.2s ease-in-out;

  ${tabletPortrait(css`
    min-width: 300px;
  `)};

  ${biggerPhone(css`
    min-width: unset;
  `)};
`

export const StyledNewsletterEmailInput = styled.input<StyledNewsletterProps>`
  display: ${(props) => (props.$isSuccess ? 'none' : 'block')};
  width: 100%;
  min-height: 56px;
  padding: 0 12px 0 24px;
  border: none;
  border-radius: 100px;
  margin-bottom: 0;
  background: transparent;
  color: ${colors.Black};
  font-family: 'Graphik LCG Web', sans-serif;
  font-size: 20px;
  font-weight: 500;
  outline-offset: 0;
  outline-style: none;
  outline-width: 3px;

  &:disabled {
    opacity: 1;
  }

  ${phone(css`
    font-size: 16px;
  `)};

  &::placeholder {
    color: ${colors.Black};
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-background-clip: text;
  }
`

export const StyledNewsletterEmailSuccessMessage = styled.span<StyledNewsletterProps>`
  display: ${(props) => (props.$isSuccess ? 'flex' : 'none')};
  width: 100%;
  min-height: 56px;
  align-items: center;
  padding: 0 24px;
  border: none;
  border-radius: 100px;
  margin-bottom: 0;
  background-color: ${colors.Green50};
  color: ${colors.Black};
  font-family: 'Graphik LCG Web', sans-serif;
  font-size: 20px;
  font-weight: 500;
  outline-offset: 0;
  outline-style: none;
  outline-width: 3px;
  text-align: start;

  ${phone(css`
    font-size: 16px;
    resize: vertical;
  `)};
`

export const ButtonLoader = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin: 20px auto;
  margin-right: 20px;
  animation: ${spinner} 1.4s infinite linear;
  background: transparent;
  background: linear-gradient(
    to right,
    ${colors.Gray500} 10%,
    rgb(255 255 255 / 0%) 42%
  );
  cursor: default;
  font-size: 10px;
  transform: translateZ(0);

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 50%;
    border-radius: 100% 0 0;
    background: transparent;
    content: '';
  }

  &::after {
    position: absolute;
    width: 75%;
    height: 75%;
    border-radius: 50%;
    margin: auto;
    content: '';
    inset: 0;
  }
`

export const StyledNewsletterButton = styled.button<StyledNewsletterProps>`
  min-height: 32px;
  max-height: 32px;
  padding: 0 16px;
  border: 0;
  border-radius: 100px;
  margin: 12px 12px 12px 0;
  background-color: ${colors.White};
  color: ${colors.Black};
  cursor: pointer;
  font-family: 'Graphik LCG Web', sans-serif;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  ${(props) =>
    props.$isSuccess &&
    css`
      display: none;
    `}
  ${biggerPhone(css`
    cursor: default;

    &:focus {
      outline: none;
    }
  `)}
`

export const StyledNewsletterFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const StyledNewsletterErrorMessage = styled.div`
  position: relative;
  bottom: -10px;
  display: inline-flex;
  color: ${colors.Red400};
  font-size: 10px;
  font-weight: 500;
  line-height: 110%;
  text-transform: uppercase;
`
